import type { ReactNode } from 'react';
import { createContext, useCallback, useContext, useMemo, useRef } from 'react';
import type { DataSchema } from '../types';
import type { TableDirtyFields, TableFormData } from '../components/common/Table/Table';
import { copySnapshot } from '../utils/agendaRecord/mergeNestedLists';
import type { TableRow } from '../components/common/Table/tableFunctions';
import type { FieldErrors } from 'react-hook-form';

export type FormApi = {
  trigger?: () => Promise<boolean>;
  getValues?: () => TableFormData;
  getDirtyFields: () => TableDirtyFields;
  isDirty: () => boolean;
  getErrors?: () => FieldErrors | undefined;
  resetSavedForm: () => void;
  buildSnapshot?: () => Snapshot<keyof DataSchema, DataSchema> | undefined;
};

export type RegisteredFormData = {
  formIdentifier: string;
  formItemId?: string;
  agendaId?: string;
  recordId?: string;
  templateId?: string;
  selectedRows?: string[];
  totalRows?: string[];
};

export type RegisteredForm = {
  data?: RegisteredFormData;
  api: FormApi;
  dataSchema?: DataSchema;
  snapshot?: Snapshot<keyof DataSchema, DataSchema>;
};

export type FormRole = 'main' | 'items';

export type RegisteredFormWithRole = RegisteredForm & {
  id: string;
  role: FormRole;
  mounted: boolean;
};

type NamespaceStore = Record<string, Record<string, RegisteredFormWithRole>>;

export const FORM_ROLE_MAIN: FormRole = 'main' satisfies FormRole;
export const FORM_ROLE_ITEMS: FormRole = 'items' satisfies FormRole;

export interface TableStateExtras<T extends keyof P, P extends DataSchema> {
  addedRows: TableRow<T, P>[] | undefined;
  value?: string[] | undefined;
  selectedRows?: string[];
  totalRows?: string[];
  meta?: {
    formIdentifier: string;
    agendaId?: string;
    viewId?: string;
    formItemId?: string;
    recordId?: string;
    templateId?: string;
  };
}

export interface FormStateSnapshot<P extends DataSchema> {
  values: TableFormData;
  dirtyFields: TableDirtyFields;
  isDirty: boolean;
  errors: FieldErrors;
  touchedFields: Record<string, unknown>;
  isValid: boolean;
  dataSchema: P;
}

export type Snapshot<T extends keyof P, P extends DataSchema> = FormStateSnapshot<P> & TableStateExtras<T, P>;
export type Snapshots = Snapshot<keyof DataSchema, DataSchema>[];

export type FormRegistryApi = {
  register: (namespace: string, id: string, data: RegisteredForm, role?: FormRole) => void;
  unregister: (namespace: string, id: string) => void;
  triggerAll: (namespace: string) => Promise<boolean>;
  triggerForms: (namespace: string, role?: FormRole) => Promise<boolean>;
  getAllData: (namespace: string) => [string, RegisteredFormWithRole][];
  getForms(namespace: string, role?: FormRole): RegisteredFormWithRole[];
  getMountedForms(namespace: string, role?: FormRole): RegisteredFormWithRole[];
  getMainForm(namespace: string): RegisteredFormWithRole | undefined;
  anyDirty: (namespace: string) => boolean;
  getDirtyFields: (namespace: string) => Map<string, TableDirtyFields>;
  hasDirtyFields: (namespace: string) => boolean;
  resetSavedForm: (namespace: string) => void;
  createFormSnapshot: (namespace: string, id: string) => void;
  createAllFormSnapshot: (namespace: string, role?: FormRole) => void;
  getFormSnapshot: (namespace: string, id: string) => Snapshot<keyof DataSchema, DataSchema> | undefined;
  getAllFormSnapshots: (namespace: string, role?: FormRole) => Snapshots;
  hasDirtySnapshots: (namespace: string, role?: FormRole) => boolean;
  hasValidSnapshots: (namespace: string, role?: FormRole) => boolean;
  removeFormSnapshot: (namespace: string, id: string) => void;
  removeAllFormSnapshots: (namespace: string, role?: FormRole) => void;
};

export type BoundFormRegistryApi = {
  register: (id: string, data: RegisteredForm, role?: FormRole) => void;
  unregister: (id: string) => void;
  triggerAll: () => Promise<boolean>;
  triggerForms: (role?: FormRole) => Promise<boolean>;
  getForms(role?: FormRole): RegisteredFormWithRole[];
  getMountedForms(role?: FormRole): RegisteredFormWithRole[];
  getMainForm(): RegisteredFormWithRole | undefined;
  anyDirty: () => boolean;
  getDirtyFields: () => Map<string, TableDirtyFields>;
  hasDirtyFields: () => boolean;
  resetSavedForm: () => void;
  createFormSnapshot: (id: string) => void;
  createAllFormSnapshot: (role?: FormRole) => void;
  getFormSnapshot: (id: string) => Snapshot<keyof DataSchema, DataSchema> | undefined;
  getAllFormSnapshots: (role?: FormRole) => Snapshots;
  hasDirtySnapshots: (role?: FormRole) => boolean;
  hasValidSnapshots: (role?: FormRole) => boolean;
  removeFormSnapshot: (id: string) => void;
  removeAllFormSnapshots: (role?: FormRole) => void;
};

const FormRegistryContext = createContext<FormRegistryApi | null>(null);

function ensure(map: NamespaceStore, namespace: string) {
  if (!map[namespace]) {
    map[namespace] = {};
  }

  return map[namespace];
}

export function FormRegistryProvider({ children }: { children: ReactNode }) {
  const storeRef = useRef<NamespaceStore>({});

  const register: FormRegistryApi['register'] = useCallback((namespace, id, data, role = 'main') => {
    const ns = ensure(storeRef.current, namespace);
    ns[id] = { ...data, id, role, mounted: true };
  }, []);

  const unregister: FormRegistryApi['unregister'] = useCallback((namespace, id) => {
    const ns = storeRef.current[namespace];
    const form = ns[id];
    form.mounted = false;
  }, []);

  const triggerAll: FormRegistryApi['triggerAll'] = useCallback(async (namespace) => {
    const ns = ensure(storeRef.current, namespace);
    const forms = Object.values(ns);
    if (!forms.length) return true;

    const results = await Promise.all(
      forms.map((f) => {
        const fn = f.api.trigger;
        return typeof fn === 'function' ? fn() : Promise.resolve(true);
      })
    );
    return results.every(Boolean);
  }, []);

  const triggerForms = useCallback(async (namespace: string, role?: FormRole) => {
    const ns = storeRef.current[namespace];
    if (!ns) return true;
    const forms = role ? Object.values(ns).filter((f) => f.role === role) : Object.values(ns);
    if (!forms.length) return true;

    const results = await Promise.all(
      forms.map((f) => {
        const fn = f.api.trigger;
        return typeof fn === 'function' ? fn() : Promise.resolve(true);
      })
    );
    return results.every((result) => result);
  }, []);

  const getAllData: FormRegistryApi['getAllData'] = useCallback((namespace) => {
    const ns = storeRef.current[namespace];
    return Object.entries(ns);
  }, []);

  const getForms: FormRegistryApi['getForms'] = useCallback((namespace: string, role?: FormRole) => {
    const ns = storeRef.current[namespace];
    if (!ns) return [];

    return role ? Object.values(ns).filter((f) => f.role === role) : Object.values(ns);
  }, []);

  const getMountedForms: FormRegistryApi['getMountedForms'] = useCallback(
    (namespace: string, role?: FormRole) => {
      const forms = getForms(namespace, role);
      return forms.filter((f) => f.mounted);
    },
    [getForms]
  );

  const getMainForm: FormRegistryApi['getMainForm'] = useCallback((namespace: string) => getForms(namespace, 'main')?.[0], [getForms]);

  const anyDirty: FormRegistryApi['anyDirty'] = useCallback((namespace) => {
    const ns = storeRef.current[namespace];
    if (!ns) return false;
    for (const f of Object.values(ns)) {
      if (f.api.isDirty()) {
        return true;
      }
    }
    return false;
  }, []);

  const getDirtyFields: FormRegistryApi['getDirtyFields'] = useCallback((namespace) => {
    const allDirtyFields = new Map<string, TableDirtyFields>();

    const ns = storeRef.current[namespace];
    if (!ns) return allDirtyFields;

    for (const f of Object.values(ns)) {
      const dirtyFields = f.api.getDirtyFields();
      if (Object.keys(dirtyFields).length) {
        allDirtyFields.set(f.id, dirtyFields);
      }
    }
    return allDirtyFields;
  }, []);

  const hasDirtyFields: FormRegistryApi['hasDirtyFields'] = useCallback((namespace) => getDirtyFields(namespace).size > 0, [getDirtyFields]);

  const resetSavedForm: FormRegistryApi['resetSavedForm'] = useCallback((namespace) => {
    const ns = storeRef.current[namespace];
    if (!ns) return;
    for (const f of Object.values(ns)) f.api.resetSavedForm();
  }, []);

  const createFormSnapshot: FormRegistryApi['createFormSnapshot'] = useCallback((namespace, id) => {
    const ns = storeRef.current[namespace];
    const form = ns[id];

    const snapshot = form.api.buildSnapshot?.();
    if (!snapshot) return;

    form.snapshot = copySnapshot<keyof DataSchema, DataSchema>(snapshot);
  }, []);

  const createAllFormSnapshot: FormRegistryApi['createAllFormSnapshot'] = useCallback(
    (namespace: string, role?: FormRole) => {
      const ns = storeRef.current[namespace];
      if (!ns) return;
      const forms = role ? Object.values(ns).filter((f) => f.role === role) : Object.values(ns);
      for (const form of forms) {
        createFormSnapshot(namespace, form.id);
      }
    },
    [createFormSnapshot]
  );

  const getFormSnapshot: FormRegistryApi['getFormSnapshot'] = useCallback((namespace, id) => {
    const ns = storeRef.current[namespace];
    if (!ns) return;

    const form = ns[id];
    if (!form) return;

    if (!form.snapshot) return;

    return form.snapshot;
  }, []);

  const getAllFormSnapshots: FormRegistryApi['getAllFormSnapshots'] = useCallback((namespace, role?) => {
    const ns = storeRef.current[namespace];
    if (!ns) return [];
    const forms = role ? Object.values(ns).filter((f) => f.role === role) : Object.values(ns);
    return forms.map((form) => form.snapshot).filter((snapshot): snapshot is Snapshot<keyof DataSchema, DataSchema> => Boolean(snapshot));
  }, []);

  const hasDirtySnapshots: FormRegistryApi['hasDirtySnapshots'] = useCallback((namespace, role) => {
    const ns = storeRef.current[namespace];
    if (!ns) return false;
    const forms = role ? Object.values(ns).filter((f) => f.role === role) : Object.values(ns);

    return Object.values(forms).some((form) => form.snapshot?.isDirty);
  }, []);

  const hasValidSnapshots: FormRegistryApi['hasValidSnapshots'] = useCallback((namespace, role) => {
    const ns = storeRef.current[namespace];
    if (!ns) return true;
    const forms = role ? Object.values(ns).filter((f) => f.role === role) : Object.values(ns);

    return Object.values(forms).every((form) => form.snapshot?.isValid ?? true);
  }, []);

  const removeFormSnapshot: FormRegistryApi['removeFormSnapshot'] = useCallback((namespace, id) => {
    const ns = storeRef.current[namespace];
    const form = ns[id];

    delete form.snapshot;
  }, []);

  const removeAllFormSnapshots: FormRegistryApi['removeAllFormSnapshots'] = useCallback(
    (namespace: string, role?: FormRole) => {
      const ns = storeRef.current[namespace];
      if (!ns) return;
      const forms = role ? Object.values(ns).filter((f) => f.role === role) : Object.values(ns);
      for (const form of forms) {
        removeFormSnapshot(namespace, form.id);
      }
    },
    [removeFormSnapshot]
  );

  const api = useMemo<FormRegistryApi>(
    () => ({
      register,
      unregister,
      triggerAll,
      triggerForms,
      getAllData,
      getForms,
      getMountedForms,
      getMainForm,
      anyDirty,
      getDirtyFields,
      hasDirtyFields,
      resetSavedForm,
      createFormSnapshot,
      createAllFormSnapshot,
      getFormSnapshot,
      getAllFormSnapshots,
      hasDirtySnapshots,
      hasValidSnapshots,
      removeFormSnapshot,
      removeAllFormSnapshots,
    }),
    [
      register,
      unregister,
      triggerAll,
      triggerForms,
      getAllData,
      getForms,
      getMountedForms,
      getMainForm,
      anyDirty,
      getDirtyFields,
      hasDirtyFields,
      resetSavedForm,
      createFormSnapshot,
      createAllFormSnapshot,
      getFormSnapshot,
      getAllFormSnapshots,
      hasDirtySnapshots,
      hasValidSnapshots,
      removeFormSnapshot,
      removeAllFormSnapshots,
    ]
  );

  return <FormRegistryContext.Provider value={api}>{children}</FormRegistryContext.Provider>;
}

export function useFormRegistry(): FormRegistryApi;
export function useFormRegistry(namespace: string): BoundFormRegistryApi;
export function useFormRegistry(namespace?: string) {
  const ctx = useContext(FormRegistryContext);
  if (!ctx) throw new Error('useFormRegistry must be used within FormRegistryProvider');

  const boundApi = useMemo<BoundFormRegistryApi>(
    () => ({
      register: (id, data, role) => ctx.register(namespace ?? '', id, data, role),
      unregister: (id) => ctx.unregister(namespace ?? '', id),
      triggerAll: () => ctx.triggerAll(namespace ?? ''),
      triggerForms: (role?: FormRole) => ctx.triggerForms(namespace ?? '', role),
      getAllData: () => ctx.getAllData(namespace ?? ''),
      getForms: (role?: FormRole) => ctx.getForms(namespace ?? '', role),
      getMountedForms: (role?: FormRole) => ctx.getMountedForms(namespace ?? '', role),
      getMainForm: () => ctx.getMainForm(namespace ?? ''),
      anyDirty: () => ctx.anyDirty(namespace ?? ''),
      getDirtyFields: () => ctx.getDirtyFields(namespace ?? ''),
      hasDirtyFields: () => ctx.hasDirtyFields(namespace ?? ''),
      resetSavedForm: () => ctx.resetSavedForm(namespace ?? ''),
      createFormSnapshot: (id) => ctx.createFormSnapshot(namespace ?? '', id),
      createAllFormSnapshot: (role?: FormRole) => ctx.createAllFormSnapshot(namespace ?? '', role),
      getFormSnapshot: (id) => ctx.getFormSnapshot(namespace ?? '', id),
      getAllFormSnapshots: (role?: FormRole) => ctx.getAllFormSnapshots(namespace ?? '', role),
      hasDirtySnapshots: (role?: FormRole) => ctx.hasDirtySnapshots(namespace ?? '', role),
      hasValidSnapshots: (role?: FormRole) => ctx.hasValidSnapshots(namespace ?? '', role),
      removeFormSnapshot: (id) => ctx.removeFormSnapshot(namespace ?? '', id),
      removeAllFormSnapshots: (role?: FormRole) => ctx.removeAllFormSnapshots(namespace ?? '', role),
    }),
    [ctx, namespace]
  );

  return namespace ? boundApi : ctx;
}
